import { useState, useRef, useEffect, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "../../../ThemeContext";
import I from "./components/Icons";
import { TABLES as DEFAULT_TABLES, CHARTS, GROUPINGS, REPEATS, FILTERS, LAYOUTS, INSIGHTS_POS, uid, G, P, S } from "./components/constants";
import Dropdown from "./components/Dropdown";
import MiniChart from "./components/MiniChart";
import GraphCard from "./components/GraphCard";
import PageCard from "./components/PageCard";
import Section from "./components/Section";
import Preview from "./components/Preview";
import BrandingPage from "./components/BrandingPage";
import { buildTemplates } from "./components/templates";
import { apiGet, apiPost } from "../../../utils/apiClient";
import { ReportBuilderAPI } from "../../../utils/ReportBuilderAPI";

// Create context for dynamic table schemas
const TablesContext = createContext<any[]>(DEFAULT_TABLES);
export const useTables = () => useContext(TablesContext);

// Signals whether real table schemas have been loaded from a datasource
const TablesReadyContext = createContext<boolean>(false);
export const useTablesReady = () => useContext(TablesReadyContext);

interface DataFile {
    filename: string;
    size_kb: number;
    modified: string;
}

interface SavedReport {
    report_id: string;
    report_name: string;
    version: number;
    created_at: string;
    created_by: string;
    sections_count: number;
    sections: any[];
}

interface SectionTemplate {
    id: string;
    name: string;
    description?: string;
    pages?: number;
    section: any;
}

// Note: Using imported components DD (Dropdown) and MC (MiniChart) as aliases
const DD = Dropdown;
const MC = MiniChart;

// ─── App ───
export default function ReportBuilder() {
    const { theme } = useTheme();
    const location = useLocation();
    const [templates] = useState(() => buildTemplates());
    const [sections, setSections] = useState<any[]>([]);
    const [reportName, setReportName] = useState<string>("Monthly Business Review");
    const [tab, setTab] = useState<string>("builder");
    const [showTmpl, setShowTmpl] = useState<boolean>(false);
    const [savedTemplates, setSavedTemplates] = useState<SectionTemplate[]>([]);
    const [reportId, setReportId] = useState<string | null>(null);
    const [currentVersion, setCurrentVersion] = useState<number | null>(null);
    const [showLoadModal, setShowLoadModal] = useState<boolean>(false);
    const [savedReports, setSavedReports] = useState<SavedReport[]>([]);
    const [dataFiles, setDataFiles] = useState<DataFile[]>([]);
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const [loadingFiles, setLoadingFiles] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [tables, setTables] = useState<any[]>(DEFAULT_TABLES);
    const [tablesReady, setTablesReady] = useState<boolean>(false);
    const [reportingPeriods, setReportingPeriods] = useState<any[]>([]);
    const [selectedReportingPeriod, setSelectedReportingPeriod] = useState<string | null>(null);
    const [branding, setBranding] = useState<any>({
        company_name: "Company Name",
        logo_url: "",
        logo_base64: "",
        primary_color: "#6366f1",
        secondary_color: "#4a0e5c",
        accent_color: "#f59e0b",
        bg_color: "#ffffff",
        text_color: "#1a1a1a",
        muted_color: "#6b7280",
        font_family: "DM Sans, sans-serif",
        font_url: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap",
        font_sizes: { cover_title: 44, cover_subtitle: 18, cover_meta: 14, section_label: 12, page_title: 26, chart_title: 14, axis_label: 11, legend: 11, body: 13, footer: 13, trend_annotation: 11 },
        page_bg: "#ffffff",
        card_bg: "#ffffff",
        card_border: "#e5e7eb",
        header_style: "underline",
        footer_position: "bottom-left",
        chart_palette: ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899", "#14b8a6", "#a855f7"],
        entity_colors: {},
        show_cover: true,
        dark_mode: false,
        // Cover page options
        cover_image_url: "",
        cover_image_base64: "",
        cover_image_fit: "cover",
        cover_image_opacity: 100,
        cover_text_placement: "center",
        cover_title_color: "#e91e8c",
        cover_subtitle_color: "#1a1a1a",
        cover_meta_color: "#6b7280",
        cover_logo_enabled: true,
        cover_logo_placement: "top",
        cover_gradient_start: "",
        cover_gradient_end: "",
        // Section divider options
        section_image_url: "",
        section_image_base64: "",
        section_image_fit: "cover",
        section_image_opacity: 100,
        section_title_placement: "center",
        section_title_color: "#e91e8c",
        section_subtitle_color: "#6b7280",
        // Table of Contents
        enable_table_of_contents: true,
        // Financial year
        financial_year_start_month: 3,
    });

    // Load saved section templates
    useEffect(() => {
        if (showTmpl) {
            ReportBuilderAPI.getSectionTemplates()
                .then(data => setSavedTemplates(data.templates || []))
                .catch(err => console.error('Failed to load templates:', err));
        }
    }, [showTmpl]);

    // Load available data files when Data tab is selected
    useEffect(() => {
        if (tab === 'data') {
            loadDataFiles();
        }
    }, [tab]);

    // Load report from query parameter if present
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const reportIdFromUrl = searchParams.get('report_id');
        if (reportIdFromUrl) {
            loadReport(reportIdFromUrl);
        }
    }, [location.search]);

    const loadDataFiles = async () => {
        setLoadingFiles(true);
        try {
            const data = await ReportBuilderAPI.getDataFiles();
            setDataFiles(data.files || []);
        } catch (error) {
            console.error('Error loading data files:', error);
        } finally {
            setLoadingFiles(false);
        }
    };

    const selectDataFile = async (filename: string) => {
        setLoadingData(true);
        try {
            const response = await apiPost('/api/reporting/data/select', { filename });
            const result = await response.json();
            if (response.ok) {
                setSelectedFile(filename);
                // Clear all caches when data source changes
                ReportBuilderAPI.setCurrentFilename(filename);
                // Update tables from schemas
                if (result.schemas && Array.isArray(result.schemas)) {
                    setTables(result.schemas);
                    setTablesReady(true);
                }
                // Load reporting periods after selecting data file
                loadReportingPeriods();
                alert(`Loaded ${filename} - ${result.tables_loaded} tables loaded`);
            } else {
                alert(`Failed to load file: ${result.detail || 'Unknown error'}`);
            }
        } catch (error: any) {
            alert(`Error loading file: ${error.message}`);
        } finally {
            setLoadingData(false);
        }
    };

    const loadReportingPeriods = async () => {
        try {
            const data = await ReportBuilderAPI.getReportingPeriods();
            if (data.periods) {
                setReportingPeriods(data.periods);
                // Set the first (most recent) period as default
                if (data.periods.length > 0) {
                    setSelectedReportingPeriod(data.periods[0].value);
                }
            }
        } catch (error) {
            console.error('Error loading reporting periods:', error);
        }
    };

    const upSec = (id: string, d: any) => setSections(s => s.map(x => x.id === id ? d : x));
    const delSec = (id: string) => setSections(s => s.filter(x => x.id !== id));
    const dupSec = (id: string) => setSections(s => {
        const i = s.findIndex(x => x.id === id); const c = JSON.parse(JSON.stringify(s[i]));
        c.id = uid(); c.name += " (Copy)"; c.pages.forEach((p: any) => { p.id = uid(); p.graphs.forEach((g: any) => g.id = uid()); });
        return [...s.slice(0, i + 1), c, ...s.slice(i + 1)];
    });
    const moveSec = (id: string, dir: number) => setSections(s => {
        const i = s.findIndex(x => x.id === id); const j = i + dir;
        if (j < 0 || j >= s.length) return s; const a = [...s]; [a[i], a[j]] = [a[j], a[i]]; return a;
    });
    const addSec = () => {
        const ns = S("Section " + (sections.length + 1));
        // Use first valid table and field from loaded datasource
        const firstTable = tables.length > 0 ? tables[0].id : "revenue";
        const firstField = tables.length > 0 && tables[0].numeric_fields?.length > 0 
            ? tables[0].numeric_fields[0] 
            : "revenue_amount";
        ns.pages = [P("Page 1", [G("Graph 1", "stacked_bar", firstTable, firstField)], "1")];
        setSections(s => [...s, ns]);
    };
    const pasteSec = async () => {
        try {
            let text: string;
            try { text = await navigator.clipboard.readText(); }
            catch { text = prompt("Paste section JSON:") || ""; }
            if (!text.trim()) return;
            const data = JSON.parse(text);
            if (data?._type !== "zell_section" || !data.section) {
                alert("Clipboard does not contain a valid section");
                return;
            }
            const c = JSON.parse(JSON.stringify(data.section));
            c.id = uid();
            c.name += " (Pasted)";
            c.pages?.forEach((p: any) => { p.id = uid(); p.graphs?.forEach((g: any) => { g.id = uid(); }); });
            setSections(s => [...s, c]);
        } catch (e: any) {
            alert(`Paste failed: ${e.message}`);
        }
    };

    const addTemplateToReport = async (templateId: string) => {
        try {
            const response = await apiGet(`/api/reporting/templates/sections/${templateId}`);
            const data = await response.json();
            if (response.ok) {
                const section = data.section;
                // Generate new IDs for the section and its children
                section.id = uid();
                section.pages.forEach((p: any) => {
                    p.id = uid();
                    p.graphs.forEach((g: any) => g.id = uid());
                });
                setSections(s => [...s, section]);
                setShowTmpl(false);
            } else {
                alert('Failed to load template');
            }
        } catch (error: any) {
            alert(`Error loading template: ${error.message}`);
        }
    };

    /** Migrate a section from the old repeatField/additionalRepeat shape to the new repeats list. */
    const migrateSection = (s: any): any => {
        if (!s.repeats || s.repeats.length === 0) {
            const repeats: { field: string; values: null }[] = [];
            if (s.repeatField && s.repeatField !== "None")
                repeats.push({ field: s.repeatField, values: null });
            if (s.additionalRepeat && s.additionalRepeat !== "None")
                repeats.push({ field: s.additionalRepeat, values: null });
            return { ...s, repeats };
        }
        return s;
    };

    /** Sanitize sections before saving — enforces business rules that may have
     *  been violated in older saved reports (e.g. showLegend=true when grouping=None),
     *  and migrates old repeatField/additionalRepeat to the new repeats list. */
    const sanitizeSections = (raw: any[]): any[] =>
        raw.map(sec => {
            const migrated = migrateSection(sec);
            return {
                ...migrated,
                pages: (migrated.pages ?? []).map((page: any) => ({
                    ...page,
                    graphs: (page.graphs ?? []).map((g: any) => ({
                        ...g,
                        showLegend: g.grouping === "None" ? false : g.showLegend,
                    })),
                })),
            };
        });

    const saveReport = async () => {
        try {
            const datasources = selectedFile ? [selectedFile] : [];
            const dataFiles = selectedFile ? [selectedFile] : [];
            const response = await apiPost('/api/reporting/reports/save', {
                report_id: reportId,
                report_name: reportName,
                sections: sanitizeSections(sections),
                branding: branding,
                change_notes: reportId ? "Updated report" : "Initial version",
                datasources: datasources,
                data_files: dataFiles
            });
            const result = await response.json();
            if (response.ok) {
                setReportId(result.report_id);
                setCurrentVersion(result.version);
                alert(`Report saved successfully as version ${result.version}`);
            } else {
                alert(`Failed to save report: ${result.detail || 'Unknown error'}`);
            }
        } catch (error: any) {
            alert(`Error saving report: ${error.message}`);
        }
    };

    const exportConfig = async () => {
        const config = {
            report_name: reportName,
            sections: sections,
            branding: branding,
            datasources: selectedFile ? [selectedFile] : [],
            exported_at: new Date().toISOString(),
        };
        try {
            await navigator.clipboard.writeText(JSON.stringify(config, null, 2));
            alert("Report configuration copied to clipboard");
        } catch {
            // Fallback for non-secure contexts
            const ta = document.createElement("textarea");
            ta.value = JSON.stringify(config, null, 2);
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            alert("Report configuration copied to clipboard");
        }
    };

    const importConfig = async () => {
        try {
            let text: string;
            try {
                text = await navigator.clipboard.readText();
            } catch {
                text = prompt("Paste report configuration JSON:") || "";
            }
            if (!text.trim()) return;
            const config = JSON.parse(text);
            if (!config.sections || !Array.isArray(config.sections)) {
                alert("Invalid config: missing sections array");
                return;
            }
            // Rehydrate IDs and migrate legacy repeat fields
            const loaded = config.sections.map((s: any) => {
                const migrated = migrateSection(s);
                migrated.id = uid();
                migrated.pages?.forEach((p: any) => {
                    p.id = uid();
                    p.graphs?.forEach((g: any) => (g.id = uid()));
                });
                return migrated;
            });
            setSections(loaded);
            if (config.report_name) setReportName(config.report_name);
            if (config.branding) setBranding(config.branding);
            if (config.datasources?.[0]) setSelectedFile(config.datasources[0]);
            setReportId(null);
            setCurrentVersion(null);
            alert(`Imported "${config.report_name || "report"}" — ${loaded.length} sections`);
        } catch (e: any) {
            alert(`Import failed: ${e.message}`);
        }
    };

    const loadSavedReports = async () => {
        try {
            const data = await ReportBuilderAPI.getSavedReports();
            setSavedReports(data.reports || []);
            setShowLoadModal(true);
        } catch (error: any) {
            alert(`Error loading reports: ${error.message}`);
        }
    };

    const deleteReport = async (reportId: string, reportName: string) => {
        if (!window.confirm(`Delete "${reportName}"? This will remove all versions and cannot be undone.`)) return;
        try {
            const response = await fetch(`/api/reporting/reports/saved/${reportId}`, { method: "DELETE" });
            if (response.ok) {
                setSavedReports(r => r.filter(x => x.report_id !== reportId));
                // Invalidate saved reports cache after deletion
                ReportBuilderAPI.invalidateQuery('/api/reporting/reports/saved');
            } else {
                const data = await response.json().catch(() => ({}));
                alert(`Failed to delete: ${data.detail || 'Unknown error'}`);
            }
        } catch (error: any) {
            alert(`Error deleting report: ${error.message}`);
        }
    };

    const loadReport = async (reportIdToLoad: string) => {
        try {
            const response = await apiGet(`/api/reporting/reports/saved/${reportIdToLoad}`);
            const data = await response.json();
            if (response.ok) {
                setReportId(data.report_id);
                setReportName(data.report_name);
                setCurrentVersion(data.version);
                // Reconstruct sections with new IDs and migrate legacy repeat fields
                const loadedSections = data.sections.map((s: any) => {
                    const migrated = migrateSection(s);
                    migrated.id = uid();
                    migrated.pages.forEach((p: any) => {
                        p.id = uid();
                        p.graphs.forEach((g: any) => g.id = uid());
                    });
                    return migrated;
                });
                setSections(loadedSections);

                // Restore datasources if available
                if (data.datasources && data.datasources.length > 0) {
                    const datasource = data.datasources[0];
                    setSelectedFile(datasource);
                    // Automatically load the datasource
                    try {
                        await selectDataFile(datasource);
                    } catch (error: any) {
                        console.error('Failed to load datasource:', error);
                    }
                }

                // Restore branding if available (merge with defaults to preserve any missing fields)
                if (data.branding) {
                    setBranding((prev: any) => ({ ...prev, ...data.branding }));
                }

                setShowLoadModal(false);
            } else {
                alert('Failed to load report');
            }
        } catch (error: any) {
            alert(`Error loading report: ${error.message}`);
        }
    };

    const totalGraphs = sections.reduce((a: number, s: any) => a + s.pages.reduce((b: number, p: any) => b + p.graphs.length, 0), 0);
    const totalPages = sections.reduce((a: number, s: any) => a + s.pages.length, 0);
    const repSecs = sections.filter((s: any) => (s.repeats ?? []).some((r: any) => r.field && r.field !== "None")).length;

    return (
        <TablesReadyContext.Provider value={tablesReady}>
        <TablesContext.Provider value={tables}>
            <div style={{ background: theme.background, color: theme.text, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", borderBottom: `1px solid ${theme.border}`, background: theme.navbarBackground, position: "sticky", top: 0, zIndex: 40, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 30, height: 30, borderRadius: 7, background: `linear-gradient(135deg, ${theme.blue}, ${theme.purple})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: "white" }}>R</div>
                        <div>
                            <input value={reportName} onChange={e => setReportName(e.target.value)} style={{ background: "transparent", border: "none", color: theme.text, fontSize: 18, fontWeight: 700, padding: 0, outline: "none" }} />
                            <div style={{ fontSize: 13, color: theme.textSecondary }}>
                                {sections.length} sections · {totalPages} pages · {totalGraphs} graphs{repSecs > 0 ? ` · ${repSecs} repeating` : ""}
                                {currentVersion && <span style={{ marginLeft: 8, color: theme.blue }}>v{currentVersion}</span>}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                        {[{ id: "builder", l: "Builder", ic: I.Lay }, { id: "preview", l: "Preview", ic: I.Eye }, { id: "data", l: "Data", ic: I.DB }, { id: "branding", l: "Branding", ic: I.Gear }].map(t => (
                            <button key={t.id} onClick={() => setTab(t.id)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none", background: tab === t.id ? theme.activeBackground : "transparent", color: tab === t.id ? theme.activeText : theme.textSecondary }}><t.ic />{t.l}</button>
                        ))}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => setShowTmpl(!showTmpl)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.textSecondary }}><I.Gear /> Templates</button>
                        <button onClick={exportConfig} title="Copy config to clipboard" style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.textSecondary }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            Export
                        </button>
                        <button onClick={importConfig} title="Paste config from clipboard" style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.textSecondary }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
                            Import
                        </button>
                        <button onClick={loadSavedReports} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.textSecondary }}><I.DB /> Load</button>
                        <button onClick={saveReport} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 14px", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer", border: "none", background: theme.buttonBackground, color: theme.buttonText }}><I.Save /> Save</button>
                    </div>
                </header>
                {/* Template modal */}
                {showTmpl && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={() => setShowTmpl(false)}>
                    <div style={{ background: theme.cardBackground, borderRadius: 14, padding: 24, width: 440, maxHeight: "80vh", overflow: "auto", border: `1px solid ${theme.border}`, boxShadow: "0 24px 64px rgba(0,0,0,.4)" }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 3, color: theme.text }}>Report Templates</h3>
                        <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 16 }}>Choose a starting template</p>
                        {Object.entries(templates).map(([k, t]) => (
                            <button key={k} onClick={() => {
                                const clone = JSON.parse(JSON.stringify(t.sections));
                                clone.forEach((s: any) => { s.id = uid(); s.pages.forEach((p: any) => { p.id = uid(); p.graphs.forEach((g: any) => g.id = uid()); }); });
                                setSections(clone); setShowTmpl(false);
                            }} style={{ width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 9, background: theme.background, border: `1px solid ${theme.border}`, color: theme.text, cursor: "pointer", marginBottom: 6 }} onMouseEnter={e => e.currentTarget.style.borderColor = theme.blue} onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
                                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 1 }}>{t.name}</div>
                                <div style={{ fontSize: 13, color: theme.textSecondary }}>{t.desc} · {t.sections.length} sections · {(t.sections as any[]).reduce((a: number, s: any) => a + s.pages.reduce((b: number, p: any) => b + p.graphs.length, 0), 0)} graphs</div>
                            </button>
                        ))}

                        {savedTemplates.length > 0 && (
                            <>
                                <div style={{ borderTop: `1px solid ${theme.border}`, margin: "20px 0 16px", paddingTop: 20 }}>
                                    <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 3, color: theme.text }}>Saved Section Templates</h3>
                                    <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 12 }}>Add a saved section to your report</p>
                                </div>
                                {savedTemplates.map(t => (
                                    <button key={t.id} onClick={() => addTemplateToReport(t.id)} style={{ width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 9, background: theme.background, border: `1px solid ${theme.border}`, color: theme.text, cursor: "pointer", marginBottom: 6 }} onMouseEnter={e => e.currentTarget.style.borderColor = theme.blue} onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
                                        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 1 }}>{t.name}</div>
                                        <div style={{ fontSize: 13, color: theme.textSecondary }}>{t.description || 'No description'} · {t.pages} page{t.pages !== 1 ? 's' : ''}</div>
                                    </button>
                                ))}
                            </>
                        )}
                    </div>
                </div>}
                {/* Load modal */}
                {showLoadModal && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }} onClick={() => setShowLoadModal(false)}>
                    <div style={{ background: theme.cardBackground, borderRadius: 14, padding: 24, width: 500, maxHeight: "80vh", overflow: "auto", border: `1px solid ${theme.border}`, boxShadow: "0 24px 64px rgba(0,0,0,.4)" }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 3, color: theme.text }}>Load Report</h3>
                        <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 16 }}>Select a saved report to load</p>
                        {savedReports.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "32px 0", color: theme.textSecondary }}>
                                No saved reports found
                            </div>
                        ) : (
                            savedReports.map(report => (
                                <div key={report.report_id} style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "stretch" }}>
                                    <button onClick={() => loadReport(report.report_id)} style={{ flex: 1, textAlign: "left", padding: "12px 14px", borderRadius: 9, background: theme.background, border: `1px solid ${theme.border}`, color: theme.text, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.borderColor = theme.blue} onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                                            <div style={{ fontWeight: 600, fontSize: 15 }}>{report.report_name}</div>
                                            <div style={{ fontSize: 12, color: theme.blue, fontWeight: 600 }}>v{report.version}</div>
                                        </div>
                                        <div style={{ fontSize: 13, color: theme.textSecondary }}>
                                            {report.sections_count} section{report.sections_count !== 1 ? 's' : ''} ·
                                            Created by {report.created_by} ·
                                            {new Date(report.created_at).toLocaleDateString()}
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => deleteReport(report.report_id, report.report_name)}
                                        title="Delete report"
                                        style={{ padding: "0 12px", borderRadius: 9, border: `1px solid ${theme.border}`, background: theme.background, color: theme.textSecondary, cursor: "pointer", display: "flex", alignItems: "center" }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = theme.error; e.currentTarget.style.color = theme.error; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.textSecondary; }}
                                    >
                                        <I.Trash />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>}
                {/* Content */}
                <div style={{ flex: 1, overflow: "auto" }}>
                    {tab === "builder" && <div style={{ margin: "0 auto", padding: "20px 16px 80px", position: "relative" }}>
                        {/* Overlay when data isn't loaded or is loading */}
                        {!tablesReady && sections.length > 0 && (
                            <div style={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(0, 0, 0, 0.6)",
                                backdropFilter: "blur(3px)",
                                zIndex: 50,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: 8,
                            }}>
                                {loadingData ? (
                                    <div style={{
                                        background: theme.cardBackground,
                                        borderRadius: 16,
                                        padding: "32px 40px",
                                        maxWidth: 500,
                                        textAlign: "center",
                                        border: `2px solid ${theme.blue}`,
                                        boxShadow: "0 24px 64px rgba(0,0,0,.5)",
                                    }}>
                                        <div style={{ 
                                            fontSize: 48, 
                                            marginBottom: 16,
                                            animation: "spin 1s linear infinite",
                                        }}>⏳</div>
                                        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                                        <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: theme.text }}>
                                            Loading Data...
                                        </h3>
                                        <p style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.5 }}>
                                            Fetching data from the server. Please wait...
                                        </p>
                                    </div>
                                ) : (
                                    <div style={{
                                        background: theme.cardBackground,
                                        borderRadius: 16,
                                        padding: "32px 40px",
                                        maxWidth: 500,
                                        textAlign: "center",
                                        border: `2px solid ${theme.warning}`,
                                        boxShadow: "0 24px 64px rgba(0,0,0,.5)",
                                    }}>
                                        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
                                        <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: theme.text }}>
                                            Data Not Loaded
                                        </h3>
                                        <p style={{ fontSize: 14, color: theme.textSecondary, marginBottom: 20, lineHeight: 1.5 }}>
                                            Please load a data source before editing report sections. The UI may break if you try to edit graphs without loaded data and schemas.
                                        </p>
                                        {selectedFile ? (
                                            <>
                                                <div style={{ fontSize: 13, color: theme.textSecondary, marginBottom: 12, padding: "8px 12px", background: theme.background, borderRadius: 6 }}>
                                                    <strong>Selected file:</strong> {selectedFile}
                                                </div>
                                                <button
                                                    onClick={() => selectDataFile(selectedFile)}
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                        padding: "10px 20px",
                                                        borderRadius: 8,
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        cursor: "pointer",
                                                        border: "none",
                                                        background: theme.buttonBackground,
                                                        color: theme.buttonText,
                                                        marginRight: 8,
                                                    }}
                                                >
                                                    <I.DB /> Refetch Data
                                                </button>
                                            </>
                                        ) : null}
                                        <button
                                            onClick={() => setTab("data")}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                padding: "10px 20px",
                                                borderRadius: 8,
                                                fontSize: 14,
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                border: `1px solid ${theme.border}`,
                                                background: theme.cardBackground,
                                                color: theme.text,
                                            }}
                                        >
                                            Go to Data Tab
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            {sections.map((s, i) => <Section key={s.id} section={s} onUpdate={d => upSec(s.id, d)} onDelete={() => delSec(s.id)} onDuplicate={() => dupSec(s.id)} onMoveUp={() => moveSec(s.id, -1)} onMoveDown={() => moveSec(s.id, 1)} isFirst={i === 0} isLast={i === sections.length - 1} theme={theme} />)}
                        </div>
                        {!tablesReady && (
                            <div style={{ width: "100%", textAlign: "center", padding: "24px", marginTop: 14, borderRadius: 14, background: theme.cardBackground, border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: 14 }}>
                                <div style={{ marginBottom: 8, fontSize: 16 }}>📊</div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>No Datasource Loaded</div>
                                <div style={{ fontSize: 13 }}>Go to the Data tab to select a datasource before creating sections</div>
                            </div>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
                            <button onClick={addSec} disabled={!tablesReady} title={!tablesReady ? "Load a datasource first" : ""} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "16px", borderRadius: 14, cursor: tablesReady ? "pointer" : "not-allowed", background: "none", border: `2px dashed ${theme.border}`, color: theme.textSecondary, fontSize: 14, fontWeight: 600, opacity: tablesReady ? 1 : 0.5 }} onMouseEnter={e => { if (tablesReady) { e.currentTarget.style.borderColor = theme.blue; e.currentTarget.style.color = theme.blue; } }} onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.textSecondary; }}><I.Plus /> Add Section</button>
                            <button onClick={pasteSec} disabled={!tablesReady} title={!tablesReady ? "Load a datasource first" : "Paste a copied section from clipboard"} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "16px", borderRadius: 14, cursor: tablesReady ? "pointer" : "not-allowed", background: "none", border: `2px dashed ${theme.border}`, color: theme.textSecondary, fontSize: 14, fontWeight: 600, opacity: tablesReady ? 1 : 0.5 }} onMouseEnter={e => { if (tablesReady) { e.currentTarget.style.borderColor = theme.blue; e.currentTarget.style.color = theme.blue; } }} onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.color = theme.textSecondary; }}>📋 Paste Section</button>
                        </div>
                    </div>}
                    {tab === "preview" && <div style={{  margin: "0 auto" }}><Preview sections={sections} theme={theme} branding={branding} reportingPeriods={reportingPeriods} selectedReportingPeriod={selectedReportingPeriod} onReportingPeriodChange={setSelectedReportingPeriod} dataFiles={selectedFile ? [selectedFile] : undefined} reportName={reportName} /></div>}
                    {tab === "data" && <div style={{ margin: "0 auto", padding: "20px 16px", maxWidth: 900 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                            <div>
                                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 3, color: theme.text }}>Data Sources</h3>
                                <p style={{ fontSize: 14, color: theme.textSecondary }}>Select an Excel file from /app/data/reports to use for reporting</p>
                            </div>
                            <button onClick={loadDataFiles} disabled={loadingFiles} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: loadingFiles ? "default" : "pointer", border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.textSecondary, opacity: loadingFiles ? 0.5 : 1 }}>
                                <I.DB /> {loadingFiles ? "Loading..." : "Refresh"}
                            </button>
                        </div>

                        {selectedFile && (
                            <div style={{ background: theme.infoLight, border: `1px solid ${theme.info}`, borderRadius: 8, padding: "12px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{ color: theme.info, fontSize: 18 }}>✓</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: theme.info }}>Active Data Source</div>
                                    <div style={{ fontSize: 13, color: theme.info, opacity: 0.8 }}>{selectedFile}</div>
                                </div>
                            </div>
                        )}

                        {loadingFiles ? (
                            <div style={{ textAlign: "center", padding: "40px", color: theme.textSecondary }}>Loading files...</div>
                        ) : dataFiles.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "60px 20px", background: theme.cardBackground, borderRadius: 10, border: `1px solid ${theme.border}` }}>
                                <div style={{ fontSize: 48, marginBottom: 16 }}>📁</div>
                                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: theme.text }}>No Data Files Found</h3>
                                <p style={{ fontSize: 14, color: theme.textSecondary }}>Place Excel files (.xlsx, .xls) in /app/data/reports directory</p>
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                {dataFiles.map(file => (
                                    <div key={file.filename} style={{ background: theme.cardBackground, borderRadius: 10, padding: "14px 16px", border: `1px solid ${selectedFile === file.filename ? theme.blue : theme.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                                            <div style={{ fontSize: 24 }}>📊</div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2, color: theme.text }}>{file.filename}</div>
                                                <div style={{ fontSize: 13, color: theme.textSecondary }}>
                                                    {file.size_kb} KB · Modified: {new Date(file.modified).toLocaleDateString()} {new Date(file.modified).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                        <button onClick={() => selectDataFile(file.filename)} style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 14px", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none", background: selectedFile === file.filename ? theme.successLight : theme.buttonBackground, color: selectedFile === file.filename ? theme.success : theme.buttonText }}>
                                            {selectedFile === file.filename ? "✓ Selected" : "Select"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>}
                    {tab === "branding" && (
                        <BrandingPage branding={branding} onChange={setBranding} theme={theme} reportName={reportName} />
                    )}
                </div>
            </div>
        </TablesContext.Provider>
        </TablesReadyContext.Provider>
    );
}